// ** React Imports
import { useState } from "react";

import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";

// ** MUI Components
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";

// ** Icons Imports
import EyeOutline from "mdi-material-ui/EyeOutline";
import ChevronLeft from "mdi-material-ui/ChevronLeft";
import EyeOffOutline from "mdi-material-ui/EyeOffOutline";
import LockOutline from "mdi-material-ui/LockOutline";
import ShieldCheckOutline from "mdi-material-ui/ShieldCheckOutline";

// ** Third Party Imports
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Hooks
import { useAuth } from "src/hooks/useAuth";
import { pathForAuthRoute } from "src/@core/utils/urlQueryPreservation";

// ** Auth UI
import AuthShell from "src/views/pages/auth/AuthShell";

yup.setLocale({
  string: {
    email: "É necessário indicar um e-mail válido",
  },
  mixed: {
    required: "Este campo é obrigatório",
  },
});

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // ** Hooks
  const token = new URLSearchParams(location.search).get("token");
  const auth = useAuth();
  const [showError, setShowError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [completed, setCompleted] = useState(false);

  const [values, setValues] = useState({
    newPassword: "",
    showNewPassword: false,
    confirmNewPassword: "",
    showConfirmNewPassword: false,
  });

  const handleClickShowNewPassword = () => {
    setValues({ ...values, showNewPassword: !values.showNewPassword });
  };

  const handleClickShowConfirmNewPassword = () => {
    setValues({ ...values, showConfirmNewPassword: !values.showConfirmNewPassword });
  };

  const InvalidTokenNewRequest = ({ title }) => {
    return (
      <>
        {title}.{" "}
        <Link component={RouterLink} to={pathForAuthRoute("/forgot-password", location.search)}>
          Clica aqui para um novo pedido de recuperação de senha.
        </Link>
      </>
    );
  };

  const ParseResetPasswordError = ({ reason }) => {
    switch (reason) {
      case "missing_token":
        return <InvalidTokenNewRequest title="O link não parece ser válido" />;
      case "invalid_token_length":
        return <InvalidTokenNewRequest title="O link não parece ser válido" />;
      case "error_getting_data":
        return "Ocorreu um erro ao obter os dados. Efetua novamente o pedido de recuperação de senha.";
      case "invalid_token":
        return <InvalidTokenNewRequest title="O link não parece ser válido" />;
      case "token_expired":
        return <InvalidTokenNewRequest title="Este link expirou" />;
      case "error_updating_user":
        return "Ocorreu um erro ao atualizar os dados. Efetua novamente o pedido de recuperação de senha.";
      default:
        return "Ocorreu um erro desconhecido. Efetua novamente o pedido de recuperação de senha.";
    }
  };

  // /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
  const schema = yup.object().shape({
    password: yup
      .string()
      .required()
      .min(4, "Tem de ter pelo menos 4 caracteres")
      .max(16, "Deve ter no máximo 16 caracteres"),
    // .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{6,}$/, "Deve conter pelo menos 6 caracteres."),
    repeat_password: yup
      .string()
      .required()
      .min(4, "Tem de ter pelo menos 4 caracteres")
      .max(16, "Deve ter no máximo 16 caracteres")
      .oneOf([yup.ref("password"), null], "As senhas não coincidem"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    setShowError(false);
    setErrorDetails(null);

    auth.resetPassword(
      { ...data, token: token },
      (a) => {
        setShowError(true);
        setErrorDetails(a?.response?.data?.reason);
      },
      () => {
        setCompleted(true);
      }
    );
  };

  const BackToLogin = () => (
    <Link className="sp-auth__back" component={RouterLink} to={pathForAuthRoute("/login", location.search)}>
      <ChevronLeft />
      <span>Voltar ao login</span>
    </Link>
  );

  // ── Success state: password changed ────────────────────────────────
  if (completed) {
    return (
      <AuthShell
        icon={<ShieldCheckOutline />}
        eyebrow="Tudo pronto"
        title="Senha alterada! 🎉"
        subtitle="A tua nova senha já está ativa."
      >
        <div className="sp-auth__alert sp-auth__alert--success">
          <span className="sp-auth__alert-title">Tudo pronto!</span>
          Já podes iniciar sessão com a tua nova senha.
        </div>
        <Button
          className="sp-auth__submit"
          variant="contained"
          onClick={() => navigate(pathForAuthRoute("/login", location.search))}
        >
          Ir para o login
        </Button>
        <BackToLogin />
      </AuthShell>
    );
  }

  // ── Request state: set a new password ──────────────────────────────
  return (
    <AuthShell
      icon={<LockOutline />}
      eyebrow="Recuperar acesso"
      title="Definir nova senha"
      subtitle="Cria uma nova senha e repete-a no campo abaixo para confirmar."
    >
      {showError && (
        <div className="sp-auth__alert sp-auth__alert--error">
          <span className="sp-auth__alert-title">Oops!</span>
          <ParseResetPasswordError reason={errorDetails} />
        </div>
      )}

      <div className="sp-auth__alert">👉 Escolhe uma senha que consigas memorizar facilmente.</div>

      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <FormControl className="sp-auth__field" fullWidth>
          <label className="sp-auth__label" htmlFor="reset-password">
            Nova senha
          </label>
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange, onBlur } }) => (
              <OutlinedInput
                fullWidth
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                id="reset-password"
                placeholder="A tua nova senha"
                error={Boolean(errors.password)}
                type={values.showNewPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={values.showNewPassword ? "Ocultar senha" : "Mostrar senha"}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleClickShowNewPassword(!values.showNewPassword)}
                    >
                      {values.showNewPassword ? <EyeOutline /> : <EyeOffOutline />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            )}
          />
          {errors.password && <FormHelperText>{errors.password.message}</FormHelperText>}
        </FormControl>

        <FormControl className="sp-auth__field" fullWidth>
          <label className="sp-auth__label" htmlFor="reset-repeat-password">
            Repetir nova senha
          </label>
          <Controller
            name="repeat_password"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange, onBlur } }) => (
              <OutlinedInput
                fullWidth
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                id="reset-repeat-password"
                placeholder="Repete a nova senha"
                error={Boolean(errors.repeat_password)}
                type={values.showConfirmNewPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={values.showConfirmNewPassword ? "Ocultar senha" : "Mostrar senha"}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleClickShowConfirmNewPassword(!values.showConfirmNewPassword)}
                    >
                      {values.showConfirmNewPassword ? <EyeOutline /> : <EyeOffOutline />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            )}
          />
          {errors.repeat_password && <FormHelperText>{errors.repeat_password.message}</FormHelperText>}
        </FormControl>

        <Button className="sp-auth__submit" type="submit" variant="contained">
          Definir nova senha
        </Button>
      </form>

      <BackToLogin />
    </AuthShell>
  );
};

export default ResetPasswordPage;
